import * as React from 'react';
import { actions } from 'react-redux-form';
import { toast } from 'react-toastify';
import { Action, Dispatch } from 'redux';

import { AppState } from 'src';

import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { getRoute, RouteDefinitions } from '@common/route-definitions';
import { PageViewModel, OptionsViewModel } from '@models/common.models';
import { ContractSimpleViewModel } from '@models/Contract';
import { AgentSearchViewModel, RealEstateAgentViewModel } from '@models/RealEstateAgent';
import { RealEstateOfficeViewModel } from '@models/RealEstateOffice';
import { contractService } from '@services/ContractService';
import { realEstateAgentsService } from '@services/RealEstateAgentsService';
import { realEstateOfficesService } from '@services/RealEstateOfficesService';

import { RealEstateAgentState } from './realestate-agent.reducer';
import { history } from '../index';
import { getRealEstateOffice } from '../realestate-office/realestate-office.actions';
import { getAccount } from '@common/components/credit-cards/credit-card.actions';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum RealEstateAgentActionType {
  CreateRealEstateAgentFailure = 'CreateRealEstateAgentFailure',
  CreateRealEstateAgentStart = 'CreateRealEstateAgentStart',
  CreateRealEstateAgentSuccess = 'CreateRealEstateAgentSuccess',

  EditRealEstateAgentFailure = 'EditRealEstateAgentFailure',
  EditRealEstateAgentStart = 'EditRealEstateAgentStart',
  EditRealEstateAgentSuccess = 'EditRealEstateAgentSuccess',

  GetRealEstateAgentContractsFail = 'GetRealEstateAgentContractsFail',
  GetRealEstateAgentContractsStart = 'GetRealEstateAgentContractsStart',
  GetRealEstateAgentContractsSuccess = 'GetRealEstateAgentContractsSuccess',

  GetRealEstateAgentFailure = 'GetRealEstateAgentFailure',
  GetRealEstateAgentStart = 'GetRealEstateAgentStart',
  GetRealEstateAgentSuccess = 'GetRealEstateAgentSuccess',

  GetRealEstateAgentRatingsFailure = 'GetRealEstateAgentRatingsFailure',
  GetRealEstateAgentRatingsStart = 'GetRealEstateAgentRatingsStart',
  GetRealEstateAgentRatingsSuccess = 'GetRealEstateAgentRatingsSuccess',

  GetRealEstateAgentStatusesFailure = 'GetRealEstateAgentStatusesFailure',
  GetRealEstateAgentStatusesStart = 'GetRealEstateAgentStatusesStart',
  GetRealEstateAgentStatusesSuccess = 'GetRealEstateAgentStatusesSuccess',

  GetRealEstateAgentTypesFailure = 'GetRealEstateAgentTypesFailure',
  GetRealEstateAgentTypesStart = 'GetRealEstateAgentTypesStart',
  GetRealEstateAgentTypesSuccess = 'GetRealEstateAgentTypesSuccess',

  RealEstateAgentOfficeCacheFetchNewOfficeFailure = 'RealEstateAgentOfficeCacheFetchNewOfficeFailure',
  RealEstateAgentOfficeCacheFetchNewOfficeStart = 'RealEstateAgentOfficeCacheFetchNewOfficeStart',
  RealEstateAgentOfficeCacheFetchNewOfficeSuccess = 'RealEstateAgentOfficeCacheFetchNewOfficeSuccess',

  RealEstateAgentSearchFailure = 'RealEstateAgentSearchFailure',
  RealEstateAgentSearchStart = 'RealEstateAgentSearchStart',
  RealEstateAgentSearchSuccess = 'RealEstateAgentSearchSuccess',

  RealEstateAgentSetOfficeAction = 'RealEstateAgentSetOfficeAction',
  RealEstateAgentToggleViewOnly = 'RealEstateAgentToggleViewOnly'
}

interface RealEstateAgentAction extends Action {
  readonly type: RealEstateAgentActionType;
}

export interface CreateRealEstateAgentFailureAction extends RealEstateAgentAction {
  readonly errorMessage: string;
}
export interface CreateRealEstateAgentStartAction extends RealEstateAgentAction {}
export interface CreateRealEstateAgentSuccessAction extends RealEstateAgentAction {
  readonly createdID: number;
}

export interface EditRealEstateAgentFailureAction extends RealEstateAgentAction {
  readonly errorMessage: string;
}
export interface EditRealEstateAgentStartAction extends RealEstateAgentAction {}
export interface EditRealEstateAgentSuccessAction extends RealEstateAgentAction {
  readonly createdID: number;
}

export interface GetRealEstateAgentContractsFailureAction extends RealEstateAgentAction {}
export interface GetRealEstateAgentContractsStartAction extends RealEstateAgentAction {}
export interface GetRealEstateAgentContractsSuccessAction extends RealEstateAgentAction {
  readonly contracts?: PageViewModel<ContractSimpleViewModel>;
}

export interface GetRealEstateAgentFailureAction extends RealEstateAgentAction {
  readonly errorMessage: string;
}
export interface GetRealEstateAgentStartAction extends RealEstateAgentAction {
  readonly loadingAgentId: number;
}
export interface GetRealEstateAgentSuccessAction extends RealEstateAgentAction {
  readonly loadedAgent: RealEstateAgentViewModel;
  readonly viewOnlyRealEstateOffice?: RealEstateOfficeViewModel;
}

export interface GetRealEstateAgentRatingsFailureAction extends RealEstateAgentAction {
  readonly errorMessage: string;
}
export interface GetRealEstateAgentRatingsStartAction extends RealEstateAgentAction {}
export interface GetRealEstateAgentRatingsSuccessAction extends RealEstateAgentAction {
  readonly realEstateAgentRatings: Array<OptionsViewModel>;
}

export interface GetRealEstateAgentStatusesFailureAction extends RealEstateAgentAction {
  readonly errorMessage: string;
}
export interface GetRealEstateAgentStatusesStartAction extends RealEstateAgentAction {}
export interface GetRealEstateAgentStatusesSuccessAction extends RealEstateAgentAction {
  readonly realEstateAgentStatuses: Array<OptionsViewModel>;
}

export interface GetRealEstateAgentTypesFailureAction extends RealEstateAgentAction {
  readonly errorMessage: string;
}
export interface GetRealEstateAgentTypesStartAction extends RealEstateAgentAction {}
export interface GetRealEstateAgentTypesSuccessAction extends RealEstateAgentAction {
  readonly realEstateAgentTypes: Array<OptionsViewModel>;
}

export interface RealEstateAgentOfficeCacheFetchNewOfficeFailureAction extends RealEstateAgentAction {
  readonly officeId: number;
}
export interface RealEstateAgentOfficeCacheFetchNewOfficeStartAction extends RealEstateAgentAction {
  readonly officeId: number;
}
export interface RealEstateAgentOfficeCacheFetchNewOfficeSuccessAction extends RealEstateAgentAction {
  readonly officeDetails: RealEstateOfficeViewModel;
  readonly officeId: number;
}

export interface RealEstateAgentSearchFailureAction extends RealEstateAgentAction {
  readonly errorMessage: string;
}
export interface RealEstateAgentSearchStartAction extends RealEstateAgentAction {
  readonly query: string | null;
}
export interface RealEstateAgentSearchSuccessAction extends RealEstateAgentAction {
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly result: PageViewModel<AgentSearchViewModel>;
}

export interface RealEstateAgentSetOfficeAction extends RealEstateAgentAction {
  readonly loadedOffice: RealEstateOfficeViewModel;
}

export interface RealEstateAgentToggleViewOnlyAction extends RealEstateAgentAction {
  readonly realEstateAgent?: RealEstateAgentViewModel;
  readonly realEstateOffice?: RealEstateOfficeViewModel;
}

export const createRealEstateAgent = (agent: RealEstateAgentViewModel, isModal?: boolean) => {
  return async (
    dispatch: Dispatch<RealEstateAgentState>
  ): Promise<CreateRealEstateAgentFailureAction | CreateRealEstateAgentSuccessAction> => {
    dispatch(createRealEstateAgentStart());
    try {
      const id: number = (await realEstateAgentsService.create(agent)).id!;
      return dispatch(createRealEstateAgentSuccess(id, isModal));
    } catch (e) {
      return dispatch(createRealEstateAgentFailure(e.toString()));
    }
  };
};
function createRealEstateAgentFailure(message: string): CreateRealEstateAgentFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, Unable to create real estate agent. ' + message} />,
    ToastOptions.Error
  );

  return {
    type: RealEstateAgentActionType.CreateRealEstateAgentFailure,
    errorMessage: message
  };
}
function createRealEstateAgentStart(): CreateRealEstateAgentStartAction {
  return {
    type: RealEstateAgentActionType.CreateRealEstateAgentStart
  };
}
function createRealEstateAgentSuccess(id: number, isModal?: boolean): CreateRealEstateAgentSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Real Estate Agent Created Successfully'} />,
    ToastOptions.Success
  );
  if (!isModal) {
    history.push(getRoute(RouteDefinitions.realestateagent.Details, { id: id }));
  }
  return {
    type: RealEstateAgentActionType.CreateRealEstateAgentSuccess,
    createdID: id
  };
}

export function editRealEstateAgent(agent: RealEstateAgentViewModel, isModal?: boolean) {
  return async (
    dispatch: Dispatch<RealEstateAgentState>
  ): Promise<EditRealEstateAgentFailureAction | EditRealEstateAgentSuccessAction> => {
    dispatch(editRealEstateAgentStart());
    try {
      const id: number = (await realEstateAgentsService.update(agent)).id!;
      return dispatch(editRealEstateAgentSuccess(id, isModal));
    } catch (e) {
      return dispatch(editRealEstateAgentFailure(e.toString()));
    }
  };
}
function editRealEstateAgentFailure(message: string): EditRealEstateAgentFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, Unable to update real estate agent. ' + message} />,
    ToastOptions.Error
  );

  return {
    type: RealEstateAgentActionType.EditRealEstateAgentFailure,
    errorMessage: message
  };
}
function editRealEstateAgentStart(): EditRealEstateAgentStartAction {
  return {
    type: RealEstateAgentActionType.EditRealEstateAgentStart
  };
}
function editRealEstateAgentSuccess(id: number, isModal?: boolean): EditRealEstateAgentSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Real Estate Agent Updated Successfully'} />,
    ToastOptions.Success
  );
  if (!isModal) {
    history.push(getRoute(RouteDefinitions.realestateagent.Details, { id: id }));
  }
  return {
    type: RealEstateAgentActionType.EditRealEstateAgentSuccess,
    createdID: id
  };
}

export function getRealEstateAgentContracts(id?: number, pageNumber?: number, pageSize?: number) {
  return async (dispatch: Dispatch<RealEstateAgentState>) => {
    dispatch(getRealEstateAgentContractsStart());
    try {
      const response: PageViewModel<ContractSimpleViewModel> = await contractService.getContractsByRealEstateAgentId(
        id,
        pageNumber,
        pageSize
      );
      dispatch(getRealEstateAgentContractsSuccess(response));
    } catch (e) {
      dispatch(getRealEstateAgentContractsFailure());
    }
  };
}
function getRealEstateAgentContractsFailure(): GetRealEstateAgentContractsFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, Unable to get contracts for agent'} />,
    ToastOptions.Error
  );
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentContractsFail
  };
}
function getRealEstateAgentContractsStart(): GetRealEstateAgentContractsStartAction {
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentContractsStart
  };
}
function getRealEstateAgentContractsSuccess(
  contracts: PageViewModel<ContractSimpleViewModel>
): GetRealEstateAgentContractsSuccessAction {
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentContractsSuccess,
    contracts: contracts
  };
}

export function getRealEstateAgent(id?: number) {
  return async (dispatch: Dispatch<RealEstateAgentState>, getState: () => AppState) => {
    dispatch(actions.reset('realEstateAgentForm'));

    // why on earth would this function not get this parameter?
    if (!id) {
      return;
    }

    // I'm not convinced this is necessary either
    const state = getState().realEstateAgent;
    if (state.loadingAgentIds.length > 0) {
      // check if we're already loading this agent.
      if (state.loadingAgentIds.indexOf(id) !== -1) {
        return; // no action
      }
    }

    dispatch(getRealEstateAgentStart(id)); // tell the store that we're loading this, so we can make sure we don't reload the same one.

    try {
      const agent: RealEstateAgentViewModel = await realEstateAgentsService.get(id);
      let contactPreference: string = '';
      if (agent.email) {
        contactPreference = 'email';
      } else if (agent.fax) {
        contactPreference = 'fax';
      } else if (agent.usps) {
        contactPreference = 'usps';
      }
      const model = {
        ...agent,
        contactPreference
      };
      dispatch(actions.change('realEstateAgentForm', model));
      dispatch(getRealEstateAgentSuccess(agent));
    } catch (e) {
      dispatch(getRealEstateAgentFailure(e.message));
    }
  };
}
function getRealEstateAgentFailure(message: string): GetRealEstateAgentFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, Unable to get real estate agent. ' + message} />,
    ToastOptions.Error
  );
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentFailure,
    errorMessage: message
  };
}
function getRealEstateAgentStart(id: number): GetRealEstateAgentStartAction {
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentStart,
    loadingAgentId: id
  };
}
function getRealEstateAgentSuccess(
  agent: RealEstateAgentViewModel,
  office?: RealEstateOfficeViewModel
): GetRealEstateAgentSuccessAction {
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentSuccess,
    loadedAgent: agent,
    viewOnlyRealEstateOffice: office
  };
}

export function getRealEstateAgentRatings() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getRealEstateAgentRatingsStart());
    try {
      const realEstateAgents: Array<OptionsViewModel> = await realEstateAgentsService.getRatings();
      dispatch(getRealEstateAgentRatingsSuccess(realEstateAgents));
    } catch (e) {
      dispatch(getRealEstateAgentRatingsFailure(e.toString()));
    }
  };
}
function getRealEstateAgentRatingsFailure(message: string): GetRealEstateAgentRatingsFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, Unable to get real estate agent ratings. ' + message}
    />,
    ToastOptions.Error
  );
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentRatingsFailure,
    errorMessage: message
  };
}
function getRealEstateAgentRatingsStart(): GetRealEstateAgentRatingsStartAction {
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentRatingsStart
  };
}
function getRealEstateAgentRatingsSuccess(
  realEstateAgentRatings: Array<OptionsViewModel>
): GetRealEstateAgentRatingsSuccessAction {
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentRatingsSuccess,
    realEstateAgentRatings: realEstateAgentRatings
  };
}

export function getRealEstateAgentStatuses() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getRealEstateAgentStatusesStart());
    try {
      const realEstateAgents: Array<OptionsViewModel> = await realEstateAgentsService.getStatuses();
      dispatch(getRealEstateAgentStatusesSuccess(realEstateAgents));
    } catch (e) {
      dispatch(getRealEstateAgentStatusesFailure(e.toString()));
    }
  };
}
function getRealEstateAgentStatusesFailure(message: string): GetRealEstateAgentStatusesFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, Unable to get real estate agent statuses. ' + message}
    />,
    ToastOptions.Error
  );

  return {
    type: RealEstateAgentActionType.GetRealEstateAgentStatusesFailure,
    errorMessage: message
  };
}
function getRealEstateAgentStatusesStart(): GetRealEstateAgentStatusesStartAction {
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentStatusesStart
  };
}
function getRealEstateAgentStatusesSuccess(
  realEstateAgentStatuses: Array<OptionsViewModel>
): GetRealEstateAgentStatusesSuccessAction {
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentStatusesSuccess,
    realEstateAgentStatuses: realEstateAgentStatuses
  };
}

export function getRealEstateAgentTypes() {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getRealEstateAgentTypesStart());
    try {
      const realEstateAgents: Array<OptionsViewModel> = await realEstateAgentsService.getTypes();
      dispatch(getRealEstateAgentTypesSuccess(realEstateAgents));
    } catch (e) {
      dispatch(getRealEstateAgentTypesFailure(e.toString()));
    }
  };
}
function getRealEstateAgentTypesFailure(message: string): GetRealEstateAgentTypesFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, Unable to get real estate agent types. ' + message}
    />,
    ToastOptions.Error
  );

  return {
    type: RealEstateAgentActionType.GetRealEstateAgentTypesFailure,
    errorMessage: message
  };
}
function getRealEstateAgentTypesStart(): GetRealEstateAgentTypesStartAction {
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentTypesStart
  };
}
function getRealEstateAgentTypesSuccess(
  realEstateAgentTypes: Array<OptionsViewModel>
): GetRealEstateAgentTypesSuccessAction {
  return {
    type: RealEstateAgentActionType.GetRealEstateAgentTypesSuccess,
    realEstateAgentTypes: realEstateAgentTypes
  };
}

export function realEstateAgentOfficeCacheFetchNewOffice(officeId: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(realEstateAgentOfficeCacheFetchNewOfficeStart(officeId));
    try {
      const officeDetails: RealEstateOfficeViewModel = await realEstateOfficesService.get(officeId);
      dispatch(realEstateAgentOfficeCacheFetchNewOfficeSuccess(officeId, officeDetails));
    } catch (e) {
      dispatch(realEstateAgentOfficeCacheFetchNewOfficeFailure(officeId));
    }
  };
}
function realEstateAgentOfficeCacheFetchNewOfficeFailure(
  officeId: number
): RealEstateAgentOfficeCacheFetchNewOfficeFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, Unable to get real estate agent cache'} />,
    ToastOptions.Error
  );
  return {
    type: RealEstateAgentActionType.RealEstateAgentOfficeCacheFetchNewOfficeFailure,
    officeId: officeId
  };
}
function realEstateAgentOfficeCacheFetchNewOfficeStart(
  officeId: number
): RealEstateAgentOfficeCacheFetchNewOfficeStartAction {
  return {
    type: RealEstateAgentActionType.RealEstateAgentOfficeCacheFetchNewOfficeStart,
    officeId: officeId
  };
}
function realEstateAgentOfficeCacheFetchNewOfficeSuccess(
  officeId: number,
  officeDetails: RealEstateOfficeViewModel
): RealEstateAgentOfficeCacheFetchNewOfficeSuccessAction {
  return {
    type: RealEstateAgentActionType.RealEstateAgentOfficeCacheFetchNewOfficeSuccess,
    officeDetails: officeDetails,
    officeId: officeId
  };
}

export function searchAgents(
  query: string | null,
  id: number | null,
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderDirection: string,
  accountId: number | null
) {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    dispatch(realEstateAgentSeachStart(query));
    try {
      if (id) {
        let searchResult: PageViewModel<AgentSearchViewModel> = await realEstateAgentsService.search(
          [id],
          undefined,
          undefined,
          undefined,
          1,
          1,
          undefined
        );
        dispatch(realEstateAgentSearchSuccess(pageNumber, pageSize, orderBy, searchResult));
        return;
      }

      let result: PageViewModel<AgentSearchViewModel> = await realEstateAgentsService.search(
        undefined,
        undefined,
        orderBy,
        orderDirection,
        pageNumber,
        pageSize,
        query || undefined,
        accountId || undefined
      );
      dispatch(realEstateAgentSearchSuccess(pageNumber, pageSize, orderBy, result));
      const storeSnapshot = getStore();
      const isInCache = (officeId: number) => {
        return storeSnapshot.realEstateAgent.realEstateAgentOfficeCache[officeId];
      };

      result.results.forEach(agent => {
        if (!isInCache(agent.officeId)) {
          dispatch(realEstateAgentOfficeCacheFetchNewOfficeStart(agent.officeId));
        }
      });
    } catch (e) {
      dispatch(realEstateAgentSearchFailure(e.toString()));
    }
  };
}
function realEstateAgentSearchFailure(errorMessage: string): RealEstateAgentSearchFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, Unable to get real estate agents. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: RealEstateAgentActionType.RealEstateAgentSearchFailure,
    errorMessage: 'Error searching agents'
  };
}
function realEstateAgentSeachStart(query: string | null): RealEstateAgentSearchStartAction {
  return {
    type: RealEstateAgentActionType.RealEstateAgentSearchStart,
    query: query
  };
}
function realEstateAgentSearchSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  result: PageViewModel<AgentSearchViewModel>
): RealEstateAgentSearchSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    result: result,
    type: RealEstateAgentActionType.RealEstateAgentSearchSuccess
  };
}

export function setAgentOffice(office: RealEstateOfficeViewModel): RealEstateAgentSetOfficeAction {
  return {
    type: RealEstateAgentActionType.RealEstateAgentSetOfficeAction,
    loadedOffice: office
  };
}

export function realEstateAgentToggleViewOnlyAction(
  agent: RealEstateAgentViewModel | null,
  office: RealEstateOfficeViewModel | null
): RealEstateAgentToggleViewOnlyAction {
  if (!agent || !office) {
    return {
      type: RealEstateAgentActionType.RealEstateAgentToggleViewOnly
    };
  }
  return {
    type: RealEstateAgentActionType.RealEstateAgentToggleViewOnly,
    realEstateAgent: agent,
    realEstateOffice: office
  };
}

export function getRealEstateOfficeFromLoadedAgent() {
  return async (dispatch: Dispatch<RealEstateAgentState>, getState: () => AppState) => {
    const state = getState();
    const rsState = state.realEstateAgent;
    if (!rsState.loadedComponentAgent) {
      // if no agent was loaded, don't do anything
      return;
    }
    if (!rsState.loadedComponentAgent.realEstateOffice) {
      // if an agent was loaded, but they don't have an office ID, don't try to load it.
      return;
    }
    const agentId = rsState.loadedComponentAgent.realEstateOffice.id;
    dispatch(getRealEstateOffice(agentId)).then(() => {
      const newState = getState().realEstateOffice;
      if (!newState.realEstateOfficeVM) {
        // no office was loaded
        return;
      }
      dispatch(setAgentOffice(newState.realEstateOfficeVM));
    }); // realEstateOfficeVM is the office to then look at and use
    return;
  };
}

export function getAccountFromLoadedAgent() {
  return async (dispatch: Dispatch<RealEstateAgentState>, getState: () => AppState) => {
    const state = getState();
    const rsState = state.realEstateAgent;
    if (!rsState.loadedComponentAgent) {
      // if no agent was loaded, don't do anything
      return;
    }
    const accountId = rsState.loadedComponentAgent.accountId;
    if (accountId) {
      dispatch(getAccount(accountId));
    }
    return;
  };
}



// WEBPACK FOOTER //
// ./src/realestate-agent/realestate-agent.actions.tsx